import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Switch,
  Select,
  Upload,
  Avatar,
  message,
  Divider,
  Row,
  Col,
  Typography,
  Space,
  Table,
  Tag,
  Modal,
  Alert,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  LockOutlined,
  BellOutlined,
  SafetyOutlined,
  ApiOutlined,
  TeamOutlined,
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  CopyOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { RootState } from '@/store';
import KycPage from './KycPage';
import WebhooksTab from '../components/settings/WebhooksTab';
import TeamTab from '../components/settings/TeamTab';
import PaymentMethodsTab from '../components/settings/PaymentMethodsTab';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const SettingsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();

  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [apiForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [apiModalVisible, setApiModalVisible] = useState(false);

  // 模拟API密钥数据
  const apiKeys = [
    {
      id: '1',
      name: t('settings.apiKeyProd'),
      key: 'pk_live_xxxxxxxxxxxx',
      created_at: '2024-01-15',
      last_used: '2024-01-20',
      status: 'active',
    },
    {
      id: '2',
      name: t('settings.apiKeyTest'),
      key: 'pk_test_xxxxxxxxxxxx',
      created_at: '2024-01-10',
      last_used: '2024-01-19',
      status: 'active',
    },
  ];

  const handleUpdateProfile = async (values: any) => {
    setLoading(true);
    try {
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success(t('settings.profileUpdated'));
    } catch (error) {
      message.error(t('settings.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values: any) => {
    setLoading(true);
    try {
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success(t('settings.passwordChanged'));
      passwordForm.resetFields();
    } catch (error) {
      message.error(t('settings.passwordChangeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async (values: any) => {
    try {
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success(t('settings.apiKeyCreated'));
      setApiModalVisible(false);
      apiForm.resetFields();
    } catch (error) {
      message.error(t('settings.createApiKeyFailed'));
    }
  };

  const handleCopyApiKey = (key: string) => {
    navigator.clipboard.writeText(key);
    message.success(t('settings.apiKeyCopied'));
  };

  const apiKeyColumns = [
    {
      title: t('settings.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('settings.apiKey'),
      dataIndex: 'key',
      key: 'key',
      render: (key: string) => (
        <Space>
          <Text code>{key}</Text>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopyApiKey(key)}
          />
        </Space>
      ),
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {status === 'active' ? t('settings.statusActive') : t('settings.statusDisabled')}
        </Tag>
      ),
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: t('settings.lastUsed'),
      dataIndex: 'last_used',
      key: 'last_used',
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" icon={<EditOutlined />}>
            {t('common.edit')}
          </Button>
          <Button size="small" danger icon={<DeleteOutlined />}>
            {t('common.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="settings-page">
      <Title level={2}>{t('settings.title')}</Title>

      <Card>
        <Tabs defaultActiveKey="profile">
          {/* 个人资料 */}
          <TabPane tab={<span><UserOutlined />{t('settings.tabProfile')}</span>} key="profile">
            <Row gutter={24}>
              <Col xs={24} md={8}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <Avatar size={120} icon={<UserOutlined />} src={user?.avatar} />
                  <div style={{ marginTop: 16 }}>
                    <Upload showUploadList={false}>
                      <Button icon={<UploadOutlined />}>{t('settings.changeAvatar')}</Button>
                    </Upload>
                  </div>
                </div>
              </Col>
              <Col xs={24} md={16}>
                <Form
                  form={profileForm}
                  layout="vertical"
                  onFinish={handleUpdateProfile}
                  initialValues={{
                    first_name: user?.first_name,
                    last_name: user?.last_name,
                    email: user?.email,
                    phone: user?.phone,
                  }}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="first_name"
                        label={t('settings.firstName')}
                        rules={[{ required: true, message: t('settings.firstNameRequired') }]}
                      >
                        <Input prefix={<UserOutlined />} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="last_name"
                        label={t('settings.lastName')}
                        rules={[{ required: true, message: t('settings.lastNameRequired') }]}
                      >
                        <Input prefix={<UserOutlined />} />
                      </Form.Item>
                    </Col>
                  </Row>
                  
                  <Form.Item
                    name="email"
                    label={t('settings.emailLabel')}
                    rules={[
                      { required: true, message: t('settings.emailRequired') },
                      { type: 'email', message: t('settings.emailInvalid') }
                    ]}
                  >
                    <Input prefix={<MailOutlined />} />
                  </Form.Item>

                  <Form.Item
                    name="phone"
                    label={t('settings.phoneLabel')}
                  >
                    <Input prefix={<PhoneOutlined />} />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      {t('common.saveChanges')}
                    </Button>
                  </Form.Item>
                </Form>
              </Col>
            </Row>
          </TabPane>

          {/* 安全设置 */}
          <TabPane tab={<span><SafetyOutlined />{t('settings.tabSecurity')}</span>} key="security">
            <div style={{ maxWidth: 600 }}>
              <Title level={4}>{t('settings.changePassword')}</Title>
              <Form
                form={passwordForm}
                layout="vertical"
                onFinish={handleChangePassword}
              >
                <Form.Item
                  name="current_password"
                  label={t('settings.currentPassword')}
                  rules={[{ required: true, message: t('settings.currentPasswordRequired') }]}
                >
                  <Input.Password prefix={<LockOutlined />} />
                </Form.Item>

                <Form.Item
                  name="new_password"
                  label={t('settings.newPassword')}
                  rules={[
                    { required: true, message: t('settings.newPasswordRequired') },
                    { min: 8, message: t('settings.passwordMin8') }
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} />
                </Form.Item>

                <Form.Item
                  name="confirm_password"
                  label={t('settings.confirmNewPassword')}
                  dependencies={['new_password']}
                  rules={[
                    { required: true, message: t('settings.confirmNewPasswordRequired') },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('new_password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t('settings.passwordMismatch')));
                      },
                    }),
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} />
                </Form.Item>

                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {t('settings.changePassword')}
                  </Button>
                </Form.Item>
              </Form>

              <Divider />

              <Title level={4}>{t('settings.twoFactor')}</Title>
              <Paragraph type="secondary">
                {t('settings.twoFactorDesc')}
              </Paragraph>
              <Space>
                <Switch defaultChecked={false} />
                <Text>{t('settings.enableTwoFactor')}</Text>
              </Space>
            </div>
          </TabPane>

          {/* 通知设置 */}
          <TabPane tab={<span><BellOutlined />{t('settings.tabNotifications')}</span>} key="notifications">
            <div style={{ maxWidth: 600 }}>
              <Title level={4}>{t('settings.emailNotifications')}</Title>
              <div style={{ marginBottom: 16 }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                  <Text>{t('settings.notifyNewOrder')}</Text>
                  <Switch defaultChecked />
                </Row>
                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                  <Text>{t('settings.notifyPaymentSuccess')}</Text>
                  <Switch defaultChecked />
                </Row>
                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                  <Text>{t('settings.notifyRefund')}</Text>
                  <Switch defaultChecked />
                </Row>
                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                  <Text>{t('settings.notifySystemMaintenance')}</Text>
                  <Switch defaultChecked />
                </Row>
              </div>

              <Divider />

              <Title level={4}>{t('settings.smsNotifications')}</Title>
              <div>
                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                  <Text>{t('settings.notifyLoginCode')}</Text>
                  <Switch defaultChecked />
                </Row>
                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                  <Text>{t('settings.notifyAbnormalLogin')}</Text>
                  <Switch defaultChecked />
                </Row>
              </div>
            </div>
          </TabPane>

          {/* API密钥 */}
          <TabPane tab={<span><ApiOutlined />{t('settings.tabApiKeys')}</span>} key="api">
            <div>
              <div style={{ marginBottom: 16 }}>
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setApiModalVisible(true)}
                  >
                    {t('settings.createApiKey')}
                  </Button>
                </Space>
              </div>

              <Alert
                message={t('settings.apiSecurityTitle')}
                description={t('settings.apiSecurityDesc')}
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Table
                columns={apiKeyColumns}
                dataSource={apiKeys || []}
                rowKey="id"
                pagination={false}
                locale={{ emptyText: t('common.noData') }}
              />
            </div>
          </TabPane>

          {/* 支付方式 */}
          <TabPane tab={<span><CreditCardOutlined />{t('settings.tabPaymentMethods')}</span>} key="payment-methods">
            <PaymentMethodsTab />
          </TabPane>

          {/* Webhooks / 开发者 */}
          <TabPane tab={<span><ThunderboltOutlined />{t('settings.tabWebhooks')}</span>} key="webhooks">
            <WebhooksTab />
          </TabPane>

          {/* 团队与角色 */}
          <TabPane tab={<span><TeamOutlined />{t('settings.tabTeam')}</span>} key="team">
            <TeamTab />
          </TabPane>

          {/* 实名认证 (KYC) */}
          <TabPane tab={<span><SafetyCertificateOutlined />{t('settings.tabKyc')}</span>} key="kyc">
            <KycPage />
          </TabPane>
        </Tabs>
      </Card>

      {/* 创建API密钥模态框 */}
      <Modal
        title={t('settings.createApiKey')}
        open={apiModalVisible}
        onCancel={() => setApiModalVisible(false)}
        footer={null}
      >
        <Form
          form={apiForm}
          layout="vertical"
          onFinish={handleCreateApiKey}
        >
          <Form.Item
            name="name"
            label={t('settings.keyName')}
            rules={[{ required: true, message: t('settings.keyNameRequired') }]}
          >
            <Input placeholder={t('settings.keyNamePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="environment"
            label={t('settings.environment')}
            rules={[{ required: true, message: t('settings.environmentRequired') }]}
          >
            <Select>
              <Select.Option value="live">{t('settings.envLive')}</Select.Option>
              <Select.Option value="test">{t('settings.envTest')}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setApiModalVisible(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit">
                {t('settings.createKey')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SettingsPage; 